/**
 * Dokumente → Regieberichte (TQ #337, umgestellt TQ #960).
 *
 * GET /api/org/:slug/documents/reports →
 *   { reports: TokenRegieberichtBeleg[], feedback_enabled: boolean }
 *
 * Seit TQ #960 liefert das Backend Token-Regiebericht-Belege (ERPNext, Beleg "TRB-…")
 * statt Timesheets:
 *
 *   name: string (z. B. "TRB-0003"), status: string (z. B. "Freigegeben")
 *   start_date, end_date: string (Abrechnungszeitraum, ISO-Datum)
 *   einwand_bis?: string, freigegeben_am?: string ("YYYY-MM-DD HH:MM:SS")
 *   summe_beleg_mio?: number (Summe des Belegs in Mio IT-SCE-Token)
 *   rueckmeldung?: "" | "bestaetigt" | "einwand" (Kundenrueckmeldung; Schluessel fehlt,
 *     wenn ERPNext das Feld nicht kennt -> Statusspalte zeigt dann `status`; "" ohne laufende
 *     Einwandfrist ebenso, siehe reportStatus)
 *
 * Einhaengepunkt: `renderReportActions` rendert je Zeile eine eigene Spalte "Aktionen"
 * (z. B. Freigeben/Ablehnen, platform-42). Ohne Prop entfaellt die Spalte.
 *
 * Das PDF kommt ueber GET /documents/reports/pdf?name= und wird im PdfViewerModal
 * angezeigt. Ein Knopf "Regiebericht erzeugen" ist bewusst NICHT gebaut (Weg mit
 * erp/QM offen).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../../components/ui'
import PdfViewerModal from '../../components/PdfViewerModal'
import { forbiddenText } from '../../lib/forbiddenText'
import { EMPTY_CELL, formatBookingDate, formatBookingMio } from '../../utils/tokenBookings'
import { canGiveFeedback } from '../../lib/reportFeedback'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

export interface RegieberichtBeleg {
  name: string
  status?: string
  start_date: string
  end_date: string
  einwand_bis?: string
  freigegeben_am?: string
  summe_beleg_mio?: number
  /** "" = noch keine Rueckmeldung, "bestaetigt", "einwand"; fehlt, wenn ERPNext das Feld nicht kennt. */
  rueckmeldung?: string
}

export interface ReportsProps {
  organization: Organization
  /** Optionale Zusatzaktionen je Zeile (eigene Spalte "Aktionen"); Default: keine Spalte. */
  renderReportActions?: (report: RegieberichtBeleg, ctx: { reload: () => void }) => ReactNode
}

export type ReportStatusTone = 'pending' | 'approved' | 'rejected'

/**
 * Kundenstatus eines Regiebericht-Belegs fuer die Statusspalte:
 * rueckmeldung "bestaetigt" -> Freigegeben, "einwand" -> Abgelehnt;
 * rueckmeldung "" -> Ausstehend NUR, solange die Einwandfrist laeuft (canGiveFeedback);
 * ohne einwand_bis (Zugang nicht gesetzt) oder nach Fristende -> Wert von `status`
 * (SPEC-rueckmeldung §6.1 Nr. 1: ohne Zugang nie "zur Rueckmeldung offen"; TRB-0003, 26.10.);
 * Schluessel fehlt -> Wert von `status`; unbekannter Wert -> roh.
 * Ergebnis null: weder rueckmeldung noch status vorhanden.
 */
export function reportStatus(
  r: Pick<RegieberichtBeleg, 'status' | 'rueckmeldung' | 'einwand_bis'>,
  now: Date = new Date(),
): { label: string; tone: ReportStatusTone } | null {
  const v = r.rueckmeldung?.trim().toLowerCase()
  if (v === undefined || (v === '' && !canGiveFeedback(r, now))) {
    const label = r.status?.trim()
    return label ? { label, tone: toneForLabel(label) } : null
  }
  if (v === '') return { label: 'Ausstehend', tone: 'pending' }
  if (v === 'bestaetigt') return { label: 'Freigegeben', tone: 'approved' }
  if (v === 'einwand') return { label: 'Abgelehnt', tone: 'rejected' }
  return { label: r.rueckmeldung ?? v, tone: 'pending' }
}

function toneForLabel(label: string): ReportStatusTone {
  const s = label.toLowerCase()
  if (s === 'freigegeben') return 'approved'
  if (s === 'abgelehnt' || s.startsWith('einwand') || s === 'storniert') return 'rejected'
  return 'pending'
}

/** Badge-Farben wie pages/Quotations.tsx: freigegeben gruen, abgelehnt rot, sonst gelb. */
const TONE_PALETTE: Record<ReportStatusTone, { bg: string; fg: string }> = {
  approved: { bg: '#d1fae5', fg: '#065f46' },
  rejected: { bg: '#fee2e2', fg: '#991b1b' },
  pending: { bg: '#fef3c7', fg: '#92400e' },
}

export default function Reports({ organization, renderReportActions }: ReportsProps) {
  const { api: keycloakApi } = useAuth()
  const { showToast } = useToast()
  const slugOrToken = organization.public_token || organization.slug

  const [reports, setReports] = useState<RegieberichtBeleg[]>([])
  const [loading, setLoading] = useState(true)
  // Backend meldet unavailable=no_access, wenn ERPNext dem Read-Bot das Lesen verweigert.
  const [unavailable, setUnavailable] = useState(false)
  const [viewerName, setViewerName] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const reload = () => setReloadKey((k) => k + 1)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setUnavailable(false)
      try {
        const r = await keycloakApi!.get(`/api/org/${slugOrToken}/documents/reports`)
        if (cancelled) return
        const list = r.data?.reports ?? []
        setReports(Array.isArray(list) ? list : [])
        setUnavailable(r.data?.unavailable === 'no_access')
      } catch (e) {
        if (cancelled) return
        const err = e as { response?: { status?: number; data?: { error?: string } } }
        if (err.response?.status !== 404) {
          showToast('error', forbiddenText(e) ?? `Regieberichte konnten nicht geladen werden: ${err.response?.data?.error ?? 'Unbekannt'}`)
        }
        setReports([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [slugOrToken, keycloakApi, showToast, reloadKey])

  async function loadPdf(name: string): Promise<Blob> {
    const res = await keycloakApi!.get(`/api/org/${slugOrToken}/documents/reports/pdf`, {
      responseType: 'blob', params: { name },
    })
    return res.data as Blob
  }

  const th: React.CSSProperties = { padding: '0.75rem 0.5rem' }
  const td: React.CSSProperties = { padding: '0.75rem 0.5rem', color: '#6b7280' }

  return (
    <div>
      <p style={{ margin: '0 0 1rem', color: '#6b7280', fontSize: '0.875rem' }}>
        Regieberichte weisen den Token-Verbrauch je Abrechnungszeitraum aus.
      </p>

      {loading ? (
        <p style={{ color: '#6b7280' }}>Lade …</p>
      ) : unavailable ? (
        <div role="status" style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
          Regieberichte sind für {organization.name} derzeit nicht abrufbar.
        </div>
      ) : reports.length === 0 ? (
        <div style={{ padding: '1.25rem 1rem', background: '#f9fafb', borderRadius: 6, color: '#6b7280', textAlign: 'center' }}>
          Für {organization.name} liegen noch keine Regieberichte vor.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem' }}>Beleg</th>
                <th style={th}>Zeitraum</th>
                <th style={th}>Status</th>
                <th style={th}>Einwand bis</th>
                <th style={{ ...th, textAlign: 'right' }}>Summe</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Aktion</th>
                {renderReportActions && <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Aktionen</th>}
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.name} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <code style={{ fontSize: '0.8125rem' }}>{r.name}</code>
                  </td>
                  <td style={td}>
                    {formatBookingDate(r.start_date)} – {formatBookingDate(r.end_date)}
                  </td>
                  <td style={th}>
                    {(() => {
                      const st = reportStatus(r)
                      if (!st) return EMPTY_CELL
                      const p = TONE_PALETTE[st.tone]
                      return (
                        <span style={{
                          padding: '2px 8px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                          background: p.bg, color: p.fg,
                        }}>
                          {st.label}
                        </span>
                      )
                    })()}
                  </td>
                  <td style={td}>{r.einwand_bis ? formatBookingDate(r.einwand_bis) : EMPTY_CELL}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#1f2937' }}>
                    {typeof r.summe_beleg_mio === 'number' ? formatBookingMio(r.summe_beleg_mio) : EMPTY_CELL}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => setViewerName(r.name)}
                      title="Regiebericht als PDF anzeigen"
                      style={{
                        padding: '4px 10px', border: '1px solid #d1d5db',
                        borderRadius: 4, background: '#fff', color: '#1f2937',
                        cursor: 'pointer', fontSize: '0.8125rem',
                      }}>
                      📄 Anzeigen
                    </button>
                  </td>
                  {renderReportActions && (
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>{renderReportActions(r, { reload })}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewerName && (
        <PdfViewerModal
          key={viewerName}
          title={`Regiebericht ${viewerName}`}
          filename={viewerName}
          load={() => loadPdf(viewerName)}
          onClose={() => setViewerName(null)}
        />
      )}
    </div>
  )
}
